import { CSSProperties, useCallback, useEffect, useState } from "react";
import { Thermometer, ThermometerItem } from "../../thermometer";
import { createUUID, FabrixIcon } from "../../../common";
import { CardAction, CardStats, CardStatus } from "..";
import { useIcon } from "../../../hooks";
import { Card } from "primereact/card";
import { Chip } from "primereact/chip";
import style from "react-syntax-highlighter/dist/esm/styles/hljs/a11y-dark";
import footer from "../../footer";
import { Toolbar } from "primereact/toolbar";



export interface ThermCardProps {
    title: string;
    id?: string;
    item?: any;
    tags?: string[];
    description?: string;
    icon?: FabrixIcon | string
    thermometerItems?: ThermometerItem[];
    className?: string;
    style?: CSSProperties;
    onClick?: () => void;
    startActions?: CardAction[];
    endActions?: CardAction[];
    onThermometerItemClick?: (item: ThermometerItem, index: number) => void;
    disabled?: boolean;
    sideColor?: string;
    status?: CardStatus;
    stats?: CardStats[];
    infoBlock?: CardStats[];
    onDrop?: (e: any, item?: any) => void;
    onDrag?: (e: any, item?: any) => void;
    onDragOver?: (e: any, item?: any) => void;
    draggable?: boolean;
}


/**
 * Displays a thermometer-style Fabrix card with optional stats, actions, and drag support.
 * @param props Card configuration including thermometer items, status, and callbacks.
 * @returns PrimeReact Card with embedded thermometer visualization.
 */
export function ThermCard(props: ThermCardProps) {

    const icon = useIcon();
    const [_disabled, setDisabled] = useState<boolean>(props.disabled ?? false)
    const [_thermItems, setThermItems] = useState<ThermometerItem[]>(props.thermometerItems || []);

    useEffect(() => {
        setThermItems(props.thermometerItems || [])
    },
        [props.thermometerItems]);

    useEffect(() => { setDisabled(props.disabled ?? false) }, [props.disabled])


    // I think the status needs to go here! 
    /**
     * Renders the header section including optional status indicator.
     * @returns JSX fragment describing the card title and status.
     */
    const renderTitle = () => {
        let statusStyle: CSSProperties = { border: `3px solid ${props.status?.color || 'gray'}` };
        return (
            <div className="fbx-therm-card-title">
                <div className="fbx-therm-card-title-block">
                    <span className={`${props.icon ? icon(props.icon) : ''} fbx-therm-card-title-icon`}></span>
                    <span className="fbx-therm-card-title-block-title">{props.title}</span>
                </div>
                {props.status && (
                    <div className="fbx-therm-card-status-block">
                        <div className="fbx-therm-card-status-block-status" style={statusStyle}>{props.status?.label}</div>
                    </div>
                )}
            </div>
        )
    }

    /**
     * Renders stat indicators either inline or stacked.
     * @param stats List of stat definitions to display.
     * @param mode Presentation layout for the stats.
     * @returns JSX container of stat entries.
     */
    const renderStats = (stats: CardStats[], mode: 'block' | 'line' = 'line') => {

        return (
            <div className="inline-flex">
                {
                    stats?.map((x, index) => {
                        let id =`${props.id}-${x.id ?? index}`
                        let className = `${x.icon ? icon(x.icon) : ''} fbx-therm-card-stats-icon`
                        return (<span title={x.tooltip} key={createUUID()} style={{ color: x.color }}
                            className={`fbx-therm-card-stats-item flex ${mode === 'block' ? 'flex-column' : 'flex-row'} align-items-center justify-content-center`}>

                            <span className="flex">
                                <span className={className}></span>
                                {
                                    x.label && (<span className="fbx-therm-card-stats-label">{x.label}</span>)
                                }
                            </span>
                            <span id={id} className="flex fbx-therm-card-stats-display">{x.display}</span>
                        </span>)
                    })
                }
            </div>
        )
    }



    const style: CSSProperties = props.sideColor ? { borderLeftWidth: 4, borderLeftStyle: "solid", borderLeftColor: `#${props.sideColor}` } : {}

    /**
     * Notifies consumers when items are dropped onto the card.
     * @param e Drop event payload.
     */
    const handleDrop = (e: any) => {
        if (props.onDrop) {
            props.onDrop(e, props.item)
        }
    }

    /**
     * Raises drag-over events for external handling when enabled.
     * @param e Drag-over event.
     */
    const handleDragOver = (e: any) => {
        if (props.onDragOver && props.disabled !== true) {
            props.onDragOver(e, props.item)
        }
    }

    /**
     * Raises drag events for external handling when enabled.
     * @param e Drag event payload.
     */
    const handleDrag = (e: any) => {
        if (props.onDrag && props.disabled !== true) {
            props.onDrag(e, props.item);
        }
    }

    /**
     * Triggers the card click callback when available and enabled.
     * @param e Click event emitted from the card body.
     */
    const handleClick = (e: any) => {
        if (props.onClick && props.disabled !== true) {
            props.onClick()
        }
    }

    /**
     * Executes toolbar actions without propagating clicks to the card.
     * @param e Mouse event from the action element.
     * @param action Action metadata referencing the handler.
     */
    const handleActionClick = (e: any, action: CardAction) => {
        e.stopPropagation();
        if (action.onClick && props.disabled !== true && action.disabled !== true)
            action.onClick(e, props.item)
    }

    /**
     * Renders the start-aligned action toolbar content.
     */
    const startActions = useCallback(() => {

        return (
            <div className="fbx-therm-card-actions-start">
                {
                    props.startActions?.map(x => {
                        let caption = x.iconOnly ? '' : x.caption;
                        let color = x.disabled ? 'silver' : (x.color ? x.color : 'black');
                        let className = `${x.className ?? ""} ${x.icon ? icon(x.icon) : ''} fbx-therm-card-action`

                        return (
                            <span key={createUUID()} style={{ color: color }} title={x.caption} className={className} onClick={(e) => handleActionClick(e, x)}>{caption}</span>
                        )
                    })
                }
            </div>)
    }, [_disabled, props.startActions])

    /**
     * Renders the end-aligned action toolbar content.
     */
    const endActions = useCallback(() => {

        return (
            <div className="fbx-therm-card-actions-end">
                {
                    props.endActions?.map(x => {
                        let caption = x.iconOnly ? '' : x.caption;
                        let color = x.disabled ? 'silver' : (x.color ? x.color : 'black');
                        let className = `${x.className} ${x.icon ? icon(x.icon) : ''} fbx-therm-card-action`

                        return (
                            <span key={createUUID()} title={x.caption} style={{ color: color }} className={className} onClick={(e) => handleActionClick(e, x)}>{caption}</span>
                        )
                    })
                }
            </div>)
    }, [_disabled, props.endActions])

    /**
     * Builds the card footer toolbar combining start and end actions.
     */
    const footer = useCallback(() => {
        return (
            <Toolbar start={startActions()} end={endActions()} className="fbx-therm-card-footer-toolbar" />
        )
    }, [_disabled, props.startActions, props.endActions])



    return (
        <Card onDrop={handleDrop} draggable={props.draggable} onDrag={handleDrag} onDragOver={handleDragOver} title={renderTitle} id={props.id} subTitle={props.description} style={style}
            footer={footer()} className={`${props.className ?? ''} fbx-therm-card ${props.onClick ? "card-clickable" : ''}`} onClick={handleClick}>

            {props.tags && props.tags.length > 0 && (
                <div className="fbx-therm-card-tags">
                    {
                        props.tags?.map((tag: string, index: number) => {
                            return (
                                <Chip key={`card_${index}_${tag}`} className="fbx-therm-card-tag" label={tag} />
                            )
                        })
                    }
                </div>
            )}
            {props.infoBlock && props.infoBlock.length > 0 && (
                <div className="fbx-therm-card-info">
                    {
                        renderStats(props.infoBlock || [], 'block')
                    }
                </div>)
            }
            {props.stats && props.stats.length > 0 && (
                <div className="fbx-therm-card-stats">
                    {
                        renderStats(props.stats || [])
                    }
                </div>
            )}
            {_thermItems.length > 0 && (
                <div className="fbx-therm-card-therm">
                    <Thermometer id={`therm-${props.id}`} valueStyle={{ color: "white" }} value={_thermItems} direction={"horizontal"} onClick={props.onThermometerItemClick} />
                </div>
            )}
        </Card>
    )
}